import mixpanel from 'mixpanel-browser';
import { useCallback } from 'react';
import { useClient } from 'urql';

import {
  getUserTrialType,
  isSelfSignupUser,
  useGetLoggedInUser,
} from '@revelio/auth';
import { AiSearchTrackingEvents, FeatureFlag } from '@revelio/core';
import type {
  CompanyDiscoveryAiOutput,
  CustomRoleTaxonomySelection,
  TalentDiscoveryAiFilterSearchResponseV2,
} from '@revelio/data-access';

import { useActivityTaxonomyConfig } from '../../../../shared/selection-lists/hooks/useActivityTaxonomyConfig';
import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';
import { mapTaxonomyIdToSelection } from '../../../../shared/user-preferences/utils/taxonomy-mapper';
import {
  usePageConfig,
  useTabContentConfig,
} from '../../components/PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../../components/PageConfigProvider/types';
import { FILTER_DEPENDENCIES } from '../constants/filterDependencies';
import { createDefaultFilterMap } from '../contexts/defaultFilters';
import { useFilterContext } from '../contexts/FilterProvider';
import type { CustomActivityTaxonomySelection } from '../definitions/activityTaxonomy';
import type { CustomGeographyTaxonomySelection } from '../definitions/geographyTaxonomy';
import { roleTaxonomyDefinition } from '../definitions/roleTaxonomy';
import type { CustomSkillTaxonomySelection } from '../definitions/skillTaxonomy';
import { useAiSearchCompatibilityToast } from '../hooks/useAiSearchCompatibilityToast';
import { useAllSelectionLists } from '../hooks/useAllSelectionLists';
import { useAvailableDateRangeWithTrialLag } from '../hooks/useAvailableDateRangeWithTrialLag';
import { useTrialCompanyRestrictionToast } from '../hooks/useTrialCompanyRestrictionToast';
import { useViewDateGranularity } from '../hooks/useViewDateGranularity';
import {
  type Filter,
  FilterId,
  type FilterSelectionsMap,
  FilterType,
  isMultiFilter,
  isSingleValueFilter,
  type MultiFilter,
  type TreeFilter,
} from '../types/filters';
import { getConsistencyKey } from '../utils/consistencyKey';
import { nameableSelectionNames } from '../utils/nameable-selection-names';
import { patchTreeFilterNames } from '../utils/patchTreeFilterNames';
import { adaptAiSearchFiltersForPage } from './adaptAiSearchFiltersForPage';
import { resolvePresetDateAnchor } from './resolvePresetDateAnchor';
import {
  collectUnresolvedCompanyRcids,
  resolveCompanyNamesFromApi,
} from './resolveUnknownCompanyNames';
import { restrictCompanySelectionsToAllowList } from './restrictCompaniesForTrial';
import { transformAiResponseToCustomDashFilters } from './transformAiResponseToCustomDashFilters';
import type { AiTransformUnknownFilter } from './transformAiResponseToCustomDashFilters/types';
import type { ScreenerAiTransformUnknownFilter } from './transformScreenerAiResponseToCustomDashFilters';
import { transformScreenerAiResponseToCustomDashFilters } from './transformScreenerAiResponseToCustomDashFilters';

/**
 * The AI apply dispatches REPLACE_FILTERS wholesale, bypassing the reducer's
 * filter-dependency processing. Re-apply the DATE_RANGE dependencies here so the
 * AI date lands the same way a manual edit does — notably syncing SNAPSHOT_DATE
 * to the range's start (min), so composition/snapshot pages show the AI date on
 * their snapshot axis instead of nothing. Only touches dependent date filters the
 * page already has, so unrelated pages are unaffected.
 */
const isMultiCompanyFilter = (
  filter: Filter
): filter is MultiFilter<TreeFilter> =>
  isMultiFilter(filter) && filter.wrappedFilterId === FilterId.COMPANY;

/** Both shapes the transform can emit for the geography tree (see multi.ts). */
const GEOGRAPHY_FILTER_IDS: ReadonlySet<FilterId> = new Set([
  FilterId.GEOGRAPHY,
  FilterId.MULTI_GEOGRAPHY,
]);

const syncDateFilterDependencies = (filterMap: FilterSelectionsMap): void => {
  const dateRange = filterMap.get(FilterId.DATE_RANGE);
  if (!dateRange || dateRange.type !== FilterType.DATE_RANGE) {
    return;
  }
  const dependencies = FILTER_DEPENDENCIES.get(FilterId.DATE_RANGE) ?? [];
  for (const dep of dependencies) {
    const existing = filterMap.get(dep.dependentFilterId);
    if (!existing) {
      continue;
    }
    const update = dep.transformer(dateRange, existing, filterMap);
    if (update) {
      filterMap.set(dep.dependentFilterId, {
        ...existing,
        ...update,
        // THE SNAPSHOT AN ANSWER LANDS ON is the window's END — the one place
        // this path departs from the shared transformer, which takes `min`.
        //
        // A read that answers "in 2023" quotes the most recent month of the
        // window it covered, so a snapshot at `min` shows a different month
        // than the figure the user was just given: 196,183 in the answer
        // (2023-12) against 203,356 on the page (2023-01).
        //
        // Overridden HERE rather than in the shared transformer because that
        // one also runs from the filter reducer on every manual date-range
        // edit, where `min` is the established behaviour and pairs with the
        // inverse dependency (picking a snapshot starts the range there, so
        // Over Time runs from the chosen month to today). This override does
        // not touch DATE_RANGE, so the applied window survives intact and the
        // Over Time tab still shows the whole period the answer covered.
        //
        // A null `max` is an open-ended window: the snapshot clears and the
        // page falls back to its own latest date, still the most recent month
        // the window covers.
        ...(dep.dependentFilterId === FilterId.SNAPSHOT_DATE && {
          value: dateRange.max,
        }),
        isUserSubmitted: true,
        isVisible: true,
      } as Filter);
    }
  }
};

/**
 * Which surface triggered the apply — the entry points route through this one
 * hook, so without this the `ai search applied` event can't attribute volume
 * between the assistants.
 */
export type AiSearchApplySource = 'page_assistant' | 'global_assistant';

export type ApplyAiSearchFiltersResult = {
  adaptedFilters: Filter[];
  unknownFilters: AiTransformUnknownFilter[];
  /**
   * Companies removed because the user's trial does not include them. Callers
   * on the assistant path turn this into a FAILED apply carrying the reason, so
   * the model says why instead of narrating a transient-looking error — and so
   * a figure for the surviving default companies is never passed off as the
   * answer to a question about a company that was stripped.
   */
  blockedCompanyNames: string[];
  /**
   * Selections the destination page's cap dropped — a PARTIAL success, not a
   * failure: the survivors did apply. See the command store's
   * `filterApplyAdjustment` channel.
   */
  droppedSelectionNames: string[];
};

export type ApplyScreenerAiSearchFiltersResult = {
  adaptedFilters: Filter[];
  unknownFilters: ScreenerAiTransformUnknownFilter[];
};

/**
 * Returns a callback that applies an AI Search response to the filter state:
 * transform -> role taxonomy injection -> base filter map -> adapt -> resolve names -> dispatch.
 * Callers can use the returned { adaptedFilters, unknownFilters } for context-specific
 * updates.
 */
export const useApplyAiSearchFilters = () => {
  const {
    dispatch,
    getFilterData,
    activeFilterMap,
    defaultSelectionOverrides,
    filterState,
    geographyTaxonomyId,
  } = useFilterContext();
  const selectionLists = useAllSelectionLists();
  const gqlClient = useClient();
  const isCompositionV4Enabled = useUnleashFlag(FeatureFlag.CompositionV4);
  const { revelioPageId, primaryFilter: primaryFilterConfig } =
    useTabContentConfig();
  const { config: pageConfig } = usePageConfig();
  const showCompatibilityToast = useAiSearchCompatibilityToast();
  const showTrialCompanyRestrictionToast = useTrialCompanyRestrictionToast();
  const viewGranularity = useViewDateGranularity();
  // Lag-aware latest data date for preset date-range re-resolution: a
  // 'last-year' window from the AI must anchor where THIS account's data ends
  // (trial data lag), not at the global max the server resolved against.
  //
  // Kept as the FAST PATH only. It is a render-time read, and on a cold mount
  // both of its inputs can be unresolved — see `resolvePresetDateAnchor`, which
  // the apply falls back to and which cannot race.
  const { availableDateRange } = useAvailableDateRangeWithTrialLag();
  const renderedPresetDateAnchor =
    (viewGranularity === 'month'
      ? availableDateRange?.month.max
      : availableDateRange?.week.max) ?? null;
  // Gate on self-signup rather than `!user.live`, matching the picker's own
  // gate (`trial/trialCompanyAllowList.ts`) — a broader gate would restrict
  // sales-created accounts that can currently pick companies freely.
  //
  // Derived from `loggedInUser` (a memo, impersonation overrides applied) and
  // NOT from `useUserTrialDetails`: that hook only publishes `isSelfSignupUser`
  // from an effect, and the assistant's headless subtree applies filters in a
  // LAYOUT effect — which runs first, so the flag read `undefined` there and
  // the guard silently never fired.
  const { loggedInUser } = useGetLoggedInUser();
  const restrictToTrialCompanies = isSelfSignupUser(loggedInUser);
  // Fresh base maps must know whether the activity filters exist for this
  // user, or an AI apply would drop ACTIVITY from the add-filter list.
  const activityTaxonomyConfig = useActivityTaxonomyConfig();

  const applyAiSearchFilters = useCallback(
    async (
      response: TalentDiscoveryAiFilterSearchResponseV2,
      options?: {
        /** Defaults to `merge` so AI primary selections combine with page defaults (overflow deactivated). Pass `replace` to wipe defaults. */
        primarySelectionMergeMode?: 'merge' | 'replace';
        /**
         * Preserve the destination page's current primary when the response
         * contains no primary selection. Fresh cross-UoA page links disable
         * this so an unrelated role/geography/company selection is not reused.
         */
        preserveCurrentPrimaryWhenMissing?: boolean;
        /**
         * When true, do not coerce an unknown `revelioPageId` to Talent Discovery
         * inside `adaptAiSearchFiltersForPage` (e.g. global-assistant chart preview).
         */
        skipTalentDiscoveryAdaptFallback?: boolean;
        /**
         * When true, seed the filter map from the page's EXISTING applied
         * filters (the consistency group `filterState.groupMap.get(consistencyKey)`,
         * the same source the page's filter chips render from) instead of
         * defaults, so this apply ADDS to / overrides the current filters
         * rather than replacing the whole set. Falls back to defaults when the
         * group has no applied filters yet.
         */
        mergeWithExistingFilters?: boolean;
        /**
         * When true, pair AI-resolved role/skill ids with the taxonomy they
         * were actually resolved under (the numeric
         * `response.filters.roles_tax_id` / `skills_tax_id`) and mark the
         * resulting ROLE_TAXONOMY/SKILL_TAXONOMY filter `isAiApplied` so the
         * preference sync cannot re-clobber the pairing. Role/skill ids are
         * taxonomy-relative — querying or labeling them under any other
         * taxonomy returns wrong data or a mislabeled chip.
         *
         * DEFAULT-ON for `applyRouteFilters` commands specifically (see
         * `applyRouteFilters.pairTaxonomy` in the command store) — the
         * product decision is made: an apply's ids are only ever valid under
         * the taxonomy the response resolved them with. Other
         * `applyAiSearchFilters` callers (the bridge's streamed/cached
         * config apply, custom-page generate/update) do NOT set this and
         * remain unpaired — a known follow-up of the same bug class, not
         * fixed here. Each side is additionally gated on its own presence
         * (see the checks below `hasAppliedTreeSelections`): an apply that
         * carries no role selections has nothing to protect, and pinning
         * anyway would freeze the page's adaptive taxonomy for every later
         * company change. `pairRoleTaxonomyEvenWithoutSelections` lifts those
         * gates for the one caller that legitimately has no instance to gate
         * on — the global-assistant chart preview/breakdown scope, which
         * groups BY role, skill or activity.
         */
        pairRoleTaxonomyWithResponse?: boolean;
        /**
         * Set by the assistant's `[…](offer:geo_tax)` switch to the taxonomy it
         * chose — the same id it forced the resolution under.
         *
         * The provider renders geography from `FilterId.GEOGRAPHY_TAXONOMY`, so
         * a preference written without dispatching that filter leaves the page
         * on the outgoing tree. Stated rather than detected: the assistant
         * always resolves geography FROM the preference, so comparing the two
         * matches on every geographic apply.
         */
        adoptGeographyTaxonomyId?: number;
        /**
         * Pair the role, skill and activity taxonomies even when the response
         * carries NO selection of that dimension. ONLY the global-assistant
         * chart preview scope sets this: it groups BY role, skill or
         * activity (a breakdown), so there is no instance to gate on, yet the
         * grouping must still run under the taxonomy the response came from.
         * On a real page an apply with no ids of a dimension has nothing to
         * protect, and pinning would freeze the page's adaptive taxonomy for
         * every later company change (the reducer keeps an `isAiApplied`
         * taxonomy pinned).
         */
        pairRoleTaxonomyEvenWithoutSelections?: boolean;
        /**
         * The caller consumes `blockedCompanyNames` and states the restriction
         * itself, so suppress the toast that would otherwise duplicate (and,
         * worded differently, contradict) it. Only the assistant's chart scope
         * does this — `source: 'global_assistant'` alone is NOT enough, because
         * the bridge's apply/navigate path shares that source and reports
         * nothing, which would leave the swap silent.
         */
        reportsBlockedCompaniesToCaller?: boolean;
        /** Surface attribution for the `ai search applied` event. */
        source?: AiSearchApplySource;
      }
    ): Promise<ApplyAiSearchFiltersResult> => {
      // Only a PRESET window needs re-anchoring, and only then is it worth a
      // client read — an explicit-date window carries no preset token and
      // `transformAiDateRangeToCustomDashFilter` ignores the anchor for it.
      // The rendered value is used when it is already there; otherwise the
      // client resolves it, which is the half that cannot race. Falling back
      // to `null` on failure keeps today's behaviour (server dates apply)
      // rather than dropping the window.
      const presetDateAnchor =
        renderedPresetDateAnchor ??
        (typeof response.date_range?.preset === 'string' &&
        response.date_range.preset.length > 0
          ? await resolvePresetDateAnchor({
              client: gqlClient,
              granularity: viewGranularity === 'month' ? 'month' : 'week',
              trialType: getUserTrialType(loggedInUser),
            }).catch(() => null)
          : null);

      const { filters: transformedFilters, unknownFilters } =
        transformAiResponseToCustomDashFilters(
          response,
          selectionLists,
          getFilterData,
          presetDateAnchor
        );

      // Geography ids are TAXONOMY-RELATIVE (metro 1: region/country/msa/city;
      // administrative 2: region/country/state/county). `geo_tax_id` states the
      // tree the response's geo ids were actually resolved under. If the
      // provider is on the OTHER tree by the time we apply, those ids do not
      // exist in its selection lists — and `useGeographyTaxonomySync`'s
      // `stripIncompatibleSelectionsFromGeographyTree` would quietly eat them,
      // leaving the page unfiltered with no trace. Drop them here instead, and
      // say so, so the race is visible.
      //
      // Optional-chained: cached/partial responses (and the hook's own unit
      // fixtures) can arrive without a `filters` object at all.
      const responseGeoTaxId = response.filters?.geo_tax_id;
      // A caller that MEANT to change the taxonomy says so. The page then
      // lags by construction — the switch writes the preference and
      // re-resolves in one tick, with no filter dispatch of its own — so its
      // response is not the race the drop below guards against, and the
      // filter is re-keyed further down (the same pairing role and skill get)
      // with the selections kept. See `adoptGeographyTaxonomyId`.
      //
      // Matched against the RESPONSE as well: the id is the one the switch
      // forced the resolution under, so a response carrying anything else is
      // a stale or cached artifact from before the switch and still drops.
      const adoptedGeoTaxId =
        options?.adoptGeographyTaxonomyId !== undefined &&
        options.adoptGeographyTaxonomyId === responseGeoTaxId &&
        options.adoptGeographyTaxonomyId !== geographyTaxonomyId
          ? options.adoptGeographyTaxonomyId
          : undefined;
      const geographyTaxonomyMismatched =
        typeof responseGeoTaxId === 'number' &&
        Number.isFinite(responseGeoTaxId) &&
        responseGeoTaxId !== geographyTaxonomyId &&
        adoptedGeoTaxId === undefined;
      if (geographyTaxonomyMismatched) {
        console.warn(
          `[ai-search] Dropping AI geography selections: response resolved under geography taxonomy ${responseGeoTaxId} but the active taxonomy is ${geographyTaxonomyId}.`
        );
      }
      const taxonomyCompatibleFilters = geographyTaxonomyMismatched
        ? transformedFilters.filter(
            (filter) => !GEOGRAPHY_FILTER_IDS.has(filter.id)
          )
        : transformedFilters;

      // Trial company entitlement — see `restrictCompanySelectionsToAllowList`.
      // Placed HERE because every AI path lands on this hook (the assistant's
      // bridge, chart scope, and the headless digest host behind
      // render_chart), and BEFORE `adaptAiSearchFiltersForPage` so page
      // adaptation, primary merge and rcid name resolution only ever see
      // allowed ids.
      const { filters: customDashFilters, blockedNames } =
        restrictToTrialCompanies
          ? restrictCompanySelectionsToAllowList(taxonomyCompatibleFilters)
          : { filters: taxonomyCompatibleFilters, blockedNames: [] };

      // Every surface that does NOT consume `blockedCompanyNames` must toast,
      // or the swap to the plan's default companies happens silently.
      if (options?.reportsBlockedCompaniesToCaller !== true) {
        showTrialCompanyRestrictionToast(blockedNames);
      }

      const hasRoleInstance = customDashFilters.some(
        (f) => f.id === FilterId.ROLE || f.id === FilterId.MULTI_ROLE
      );
      const hasRoleTaxonomyInstance = customDashFilters.some(
        (f) => f.id === FilterId.ROLE_TAXONOMY
      );
      // Skill twin of `hasRoleInstance`. Both read the THIS-RESPONSE transform
      // output (not the assembled map), which is what makes them usable as the
      // "did the AI actually resolve one?" signal in the taxonomy pairing gates
      // below — see the note there about merge applies.
      const hasSkillInstance = customDashFilters.some(
        (f) =>
          f.id === FilterId.CUSTOM_SKILL || f.id === FilterId.MULTI_CUSTOM_SKILL
      );
      // Activity twin of `hasRoleInstance`/`hasSkillInstance`.
      const hasActivityInstance = customDashFilters.some(
        (f) => f.id === FilterId.ACTIVITY || f.id === FilterId.MULTI_ACTIVITY
      );

      // Compute the consistency (group) key up front: the additive seed below
      // needs it to locate the page's EXISTING applied filters. The group map
      // is keyed by the consistency
      // key — which is `primaryFilterConfig.id` for company/geography/role
      // pages, the page id for screener-v2 pages, and `NO_PRIMARY_FILTER_KEY`
      // for pages with no primary filter (e.g. Talent Discovery, whose
      // applied role/company/seniority/gender filters all live under
      // `NO_PRIMARY_FILTER_KEY`). Keying by `primaryFilterConfig.id` alone
      // missed that group entirely on discovery pages, so a refinement seeded
      // from defaults and wiped the prior filters.
      const consistencyKey = getConsistencyKey({
        revelioPageId,
        primaryFilterId: primaryFilterConfig?.id,
      });

      // IMPORTANT: when resolving taxonomy filters to carry over into this
      // apply, read from the TARGET group's filter state — NOT from
      // `activeFilterMap` (which is the currently-ACTIVE group's map, and
      // during navigation is still the previous page's group). Using
      // `activeFilterMap` causes a cross-scope leak: e.g. navigating
      // COMPANY → GEOGRAPHY writes `{default: true, isUserSubmitted: true}`
      // into the GEOGRAPHY group via pref sync; then when the user
      // navigates back to COMPANY, `activeFilterMap` is still the GEOGRAPHY
      // group, we'd preserve its `{default: true, isUserSubmitted: true}`
      // into the REPLACE_FILTERS payload targeting the COMPANY group, and
      // `filtersToPreferences` on COMPANY then infers NOT-adaptive (because
      // `isUserSubmitted: true`), making the Preferences UI report the
      // user's COMPANY pref as "default" even though it's actually
      // "adaptive" on the server.
      //
      // Reading from the target group (by consistency key) preserves each
      // group's own taxonomy state independently, and falls back to
      // `baseFilterMap` defaults when the group hasn't been visited yet.
      const targetGroupFilterMap = filterState.groupMap.get(consistencyKey);

      if (hasRoleInstance && !hasRoleTaxonomyInstance) {
        // Ensure a ROLE_TAXONOMY filter travels alongside the AI-applied role.
        // Preserve the target group's existing filter AS-IS — critically
        // including `isUserSubmitted` (flipping it to `true` locks the page out
        // of adaptive mode). The adaptive VALUE itself is (re)derived from the
        // final company selection in the post-assembly block below, once
        // `adaptAiSearchFiltersForPage` has rolled-up/normalized the company
        // tree — reading the raw pre-adapt selection here gives the wrong RICS
        // level.
        const existingRoleTaxonomy = targetGroupFilterMap?.get(
          FilterId.ROLE_TAXONOMY
        );
        const hasUsableExistingTaxonomy =
          existingRoleTaxonomy &&
          existingRoleTaxonomy.type === FilterType.VALUE &&
          'value' in existingRoleTaxonomy;
        // Role ids are TAXONOMY-RELATIVE, but pairing them with the response's
        // taxonomy happens ONCE, on the FINAL assembled map (the
        // `pairRoleTaxonomyWithResponse` block below) — pairing the pushed
        // filter here is pointless because `adaptAiSearchFiltersForPage` and
        // the carry-over seeding can strip or clobber it before dispatch.
        customDashFilters.push(
          hasUsableExistingTaxonomy
            ? existingRoleTaxonomy
            : {
                ...roleTaxonomyDefinition,
                isUserSubmitted: true,
              }
        );
      }

      const baseFilterMap =
        options?.mergeWithExistingFilters && targetGroupFilterMap
          ? new Map(targetGroupFilterMap)
          : createDefaultFilterMap(
              primaryFilterConfig?.id,
              activityTaxonomyConfig,
              defaultSelectionOverrides
            );
      // Keep contradictory callers safe: an explicit fresh-primary replace
      // must remove a stale primary even if the caller also seeded secondary
      // filters from the existing map. Normal additive refinements preserve
      // the primary by leaving preserveCurrentPrimaryWhenMissing enabled.
      if (
        options?.mergeWithExistingFilters === true &&
        options.preserveCurrentPrimaryWhenMissing === false &&
        primaryFilterConfig
      ) {
        baseFilterMap.delete(primaryFilterConfig.id);
      }
      // Carry over the target group's taxonomy AND correction-preference
      // filters. Corrections (DATA_ADJUSTMENTS / CONTINGENT_WORKERS) are synced
      // per-scope from user preferences; `createDefaultFilterMap` resets them to
      // empty, so without this an AI apply leaves them unpopulated and
      // composition charts that gate on `correctionPreferencesSynced` never
      // leave their loading state.
      [
        FilterId.ROLE_TAXONOMY,
        FilterId.SKILL_TAXONOMY,
        FilterId.ACTIVITY_TAXONOMY,
        FilterId.DATA_ADJUSTMENTS,
        FilterId.CONTINGENT_WORKERS,
      ].forEach((filterId) => {
        const existing = targetGroupFilterMap?.get(filterId);
        if (existing) {
          baseFilterMap.set(filterId, existing);
        }
      });

      // Same reasoning as above: read the target group's primary filter
      // state, not the active (mid-nav, still previous scope's) map.
      const currentPrimaryFilter = primaryFilterConfig
        ? (targetGroupFilterMap?.get(primaryFilterConfig.id) ??
          baseFilterMap.get(primaryFilterConfig.id))
        : undefined;

      const adaptRevelioPageId =
        options?.skipTalentDiscoveryAdaptFallback === true
          ? revelioPageId
          : (revelioPageId ?? RevelioPageId.TALENT_DISCOVERY);

      const { adaptedFilters, modifications } = adaptAiSearchFiltersForPage({
        filters: customDashFilters,
        revelioPageId: adaptRevelioPageId,
        primaryFilterConfig,
        currentPrimaryFilter,
        primarySelectionMergeMode:
          options?.primarySelectionMergeMode ?? 'merge',
        preserveCurrentPrimaryWhenMissing:
          options?.preserveCurrentPrimaryWhenMissing ?? true,
        viewGranularity,
      });

      if (modifications.length > 0) {
        showCompatibilityToast(modifications);
      }
      // Same source AND same treatment as the toast, so the two cannot disagree:
      // deduped, and stripped of id-only names, which the cap does yield because
      // it runs before `resolveCompanyNamesFromApi` patches labels in. Empty
      // means no notice — better than "so  were left off".
      const droppedSelectionNames = nameableSelectionNames([
        ...new Set(
          modifications.flatMap(
            (modification) => modification.droppedNames ?? []
          )
        ),
      ]);

      const filterMap: FilterSelectionsMap = new Map(baseFilterMap);
      adaptedFilters.forEach((filter) => {
        filterMap.set(filter.id, filter);
      });

      // Sync SNAPSHOT_DATE ↔ DATE_RANGE (REPLACE_FILTERS below skips the reducer's
      // dependency processing), so the AI date lands on both the range and
      // snapshot axes.
      syncDateFilterDependencies(filterMap);

      // Gate each pairing on ids that SURVIVED page adaptation: an instance
      // stripped by `adaptAiSearchFiltersForPage` leaves nothing to pair, and
      // pinning the taxonomy anyway would repoint the page's other
      // role/skill-taxonomy consumers for no reason.
      const hasAppliedTreeSelections = (
        singleId: FilterId,
        multiId: FilterId
      ): boolean => {
        const applied = filterMap.get(multiId) ?? filterMap.get(singleId);
        if (!applied) return false;
        if (isMultiFilter(applied)) {
          return applied.instances.some(
            (instance) =>
              'selections' in instance && instance.selections.length > 0
          );
        }
        if (applied.type !== FilterType.TREE) return false;
        return applied.isPrimary
          ? (applied.primarySelections?.length ?? 0) > 0
          : applied.selections.length > 0;
      };

      // Pair the response taxonomy AFTER assembly: the pushed filter above
      // can be stripped by `adaptAiSearchFiltersForPage`, and the carry-over
      // block re-seeds the group's preference-synced value — either way the
      // ids' true taxonomy would lose to a stale one at query time. Nothing
      // re-resolves the ids after this function, so the paired value must
      // survive: the company-adaptive block below is gated off on paired
      // applies, and `isAiApplied` keeps the preference sync from overwriting
      // it after dispatch.
      //
      // Two modes, gated by `pairRoleTaxonomyEvenWithoutSelections`:
      //
      // - Default (page applies): the response must have supplied a role
      //   (`hasRoleInstance`) AND it must have survived adaptation
      //   (`hasAppliedTreeSelections`). An apply that carries NO role ids has
      //   nothing to protect, and pinning anyway would repoint the page's
      //   adaptive taxonomy for no reason (and freeze it, since `isAiApplied`
      //   disables later company-driven recompute — see
      //   `deriveAdaptiveTaxonomies`'s pinned branch).
      //   BOTH checks are required because they answer different questions on
      //   a MERGE apply: `hasAppliedTreeSelections` reads the ASSEMBLED map,
      //   which on merge is seeded from the page's existing group, so roles
      //   carried over from a previous turn satisfy it on their own. A
      //   date-only refinement ("last 3 years") would then re-pin the taxonomy
      //   and reinterpret those carried, differently-resolved ids under this
      //   response's `roles_tax_id`. `hasRoleInstance` reads THIS response's
      //   transform output, so it is the only signal that the AI actually
      //   resolved a role this turn.
      // - Breakdown scope (`pairRoleTaxonomyEvenWithoutSelections: true`):
      //   applied UNCONDITIONALLY. A role BREAKDOWN scope (company-only,
      //   grouping BY role: the top-N families) has no role selection by
      //   design, so gating on one let the carry-over's leaked adaptive
      //   residue (`effectiveRoleTaxonomySelection`, e.g. rics_k50 31) win —
      //   the chart then grouped/answered under a different taxonomy than
      //   the resolver used and than the user's pages run under
      //   (roles_tax_id already reflects the user's preference via
      //   useGlobalAssistantTaxonomy). `response.filters.roles_tax_id` is the
      //   one taxonomy the whole turn must share.
      if (
        options?.pairRoleTaxonomyWithResponse === true &&
        (options?.pairRoleTaxonomyEvenWithoutSelections === true ||
          (hasRoleInstance &&
            hasAppliedTreeSelections(FilterId.ROLE, FilterId.MULTI_ROLE)))
      ) {
        // Use the numeric `roles_tax_id`: it states the taxonomy the ids were
        // ACTUALLY resolved under (the string `roles_tax` merely echoes the
        // request). 0 is a valid value (= default taxonomy).
        const taxId = response.filters.roles_tax_id;
        const finalResponseTaxonomy =
          typeof taxId === 'number' && Number.isFinite(taxId)
            ? mapTaxonomyIdToSelection(taxId)
            : undefined;
        const assembledRoleTaxonomy = filterMap.get(FilterId.ROLE_TAXONOMY);
        if (
          finalResponseTaxonomy &&
          assembledRoleTaxonomy &&
          isSingleValueFilter<CustomRoleTaxonomySelection>(
            assembledRoleTaxonomy
          )
        ) {
          filterMap.set(FilterId.ROLE_TAXONOMY, {
            ...assembledRoleTaxonomy,
            value: {
              ...finalResponseTaxonomy,
              version: assembledRoleTaxonomy.value?.version,
            },
            isUserSubmitted: false,
            isAiApplied: true,
          });
        }
      }

      // SKILL twin of the role pairing above: skill ids are equally
      // taxonomy-relative, and `skills_tax_id` states the taxonomy the
      // response's skill ids were actually resolved under — without the
      // pairing they'd be queried and chip-labeled under whatever taxonomy
      // the destination scope holds. Same two modes as the role block:
      // carried-over skills alone must not re-pin on a merge apply, and the
      // breakdown scope pairs unconditionally. That scope never syncs the
      // taxonomy preference, so unpaired it groups a skill breakdown under the
      // default taxonomy, and queries a skill page's default skills — picked
      // for the preferred taxonomy — under the default one.
      if (
        options?.pairRoleTaxonomyWithResponse === true &&
        (options?.pairRoleTaxonomyEvenWithoutSelections === true ||
          (hasSkillInstance &&
            hasAppliedTreeSelections(
              FilterId.CUSTOM_SKILL,
              FilterId.MULTI_CUSTOM_SKILL
            )))
      ) {
        const skillsTaxId = response.filters.skills_tax_id;
        const assembledSkillTaxonomy = filterMap.get(FilterId.SKILL_TAXONOMY);
        if (
          typeof skillsTaxId === 'number' &&
          Number.isFinite(skillsTaxId) &&
          assembledSkillTaxonomy &&
          isSingleValueFilter<CustomSkillTaxonomySelection>(
            assembledSkillTaxonomy
          )
        ) {
          filterMap.set(FilterId.SKILL_TAXONOMY, {
            ...assembledSkillTaxonomy,
            value: {
              taxonomyId: skillsTaxId,
              version: assembledSkillTaxonomy.value?.version,
            },
            isUserSubmitted: false,
            isAiApplied: true,
          });
        }
      }

      // ACTIVITY twin of the role/skill pairings above: activity ids are
      // equally taxonomy-relative, and `activities_tax_id` states the taxonomy
      // the response's activity ids were actually resolved under — without the
      // pairing they'd be queried and chip-labeled under whatever taxonomy the
      // destination scope holds. Same two modes as the skill block.
      if (
        options?.pairRoleTaxonomyWithResponse === true &&
        (options?.pairRoleTaxonomyEvenWithoutSelections === true ||
          (hasActivityInstance &&
            hasAppliedTreeSelections(
              FilterId.ACTIVITY,
              FilterId.MULTI_ACTIVITY
            )))
      ) {
        const activitiesTaxId = response.filters.activities_tax_id;
        const assembledActivityTaxonomy = filterMap.get(
          FilterId.ACTIVITY_TAXONOMY
        );
        if (
          typeof activitiesTaxId === 'number' &&
          Number.isFinite(activitiesTaxId) &&
          assembledActivityTaxonomy &&
          isSingleValueFilter<CustomActivityTaxonomySelection>(
            assembledActivityTaxonomy
          )
        ) {
          filterMap.set(FilterId.ACTIVITY_TAXONOMY, {
            ...assembledActivityTaxonomy,
            value: {
              taxonomyId: activitiesTaxId,
              version: assembledActivityTaxonomy.value?.version,
            },
            isUserSubmitted: false,
            isAiApplied: true,
          });
        }
      }

      // Left un-rekeyed, `recalcGeographyTreeLevelIds` re-derives each
      // selection's `levelId` from ID DEPTH against the outgoing list — both
      // trees are four deep, so a metro id is silently relabelled a state and
      // shipped in the `msa` field (the trap `useGeographyTaxonomy`
      // documents), which is worse than dropping it.
      //
      // Not `isAiApplied`: this is the value the preference sync would have
      // written, so the sync must stay free to overwrite it.
      if (adoptedGeoTaxId !== undefined) {
        const assembledGeographyTaxonomy = filterMap.get(
          FilterId.GEOGRAPHY_TAXONOMY
        );
        if (
          assembledGeographyTaxonomy &&
          isSingleValueFilter<CustomGeographyTaxonomySelection>(
            assembledGeographyTaxonomy
          )
        ) {
          filterMap.set(FilterId.GEOGRAPHY_TAXONOMY, {
            ...assembledGeographyTaxonomy,
            // `version` is NOT carried over, unlike the role and skill
            // pairings: it names a version of the OUTGOING taxonomy's tree,
            // which does not exist in the incoming one. Omitted, the
            // geography tree query resolves the latest — the behaviour every
            // geography read has always had.
            value: { taxonomyId: adoptedGeoTaxId },
            isUserSubmitted: false,
          });
        } else {
          // The drop above was skipped on the promise that this re-key would
          // land, so a map without a single-value taxonomy filter would keep
          // the selections AND leave them keyed to the outgoing tree — the
          // relabelling described above, with nothing said. Say it.
          console.warn(
            `[ai-search] Kept AI geography selections for adopted taxonomy ${adoptedGeoTaxId} but the assembled filter map carries no single-value ${FilterId.GEOGRAPHY_TAXONOMY}; the page may still be keyed to ${geographyTaxonomyId}.`
          );
        }
      }

      // The adaptive role + skill taxonomy for these companies is derived in
      // the reducer when this apply's REPLACE_FILTERS lands (see
      // `deriveAdaptiveTaxonomies`) — nothing to front-load here. A response
      // taxonomy that was paired above carries `isAiApplied`, which the
      // derivation preserves.

      const unresolvedRcids = collectUnresolvedCompanyRcids(adaptedFilters);

      if (unresolvedRcids.length > 0) {
        try {
          const nameMap = await resolveCompanyNamesFromApi(
            gqlClient,
            unresolvedRcids,
            { compositionV4: isCompositionV4Enabled }
          );

          if (nameMap.size > 0) {
            const companyFilterId = filterMap.has(FilterId.MULTI_COMPANY)
              ? FilterId.MULTI_COMPANY
              : FilterId.COMPANY;
            const companyFilter = filterMap.get(companyFilterId);

            if (companyFilter) {
              filterMap.set(
                companyFilterId,
                isMultiCompanyFilter(companyFilter)
                  ? {
                      ...companyFilter,
                      instances: companyFilter.instances.map((inst) =>
                        patchTreeFilterNames(inst, nameMap)
                      ),
                    }
                  : patchTreeFilterNames(companyFilter, nameMap)
              );
            }
          }
        } catch (err) {
          console.warn(
            '[AI Search] Failed to resolve company names from API, falling back to numeric IDs',
            err
          );
        }
      }

      dispatch({
        type: 'REPLACE_FILTERS',
        payload: {
          filterMap,
          consistencyKey,
        },
      });

      const mergedUnknownFilters = [
        ...(response.unknownFilters || []),
        ...unknownFilters,
      ];

      const resolvedFilters = adaptedFilters.map(
        (f) => filterMap.get(f.id) ?? f
      );

      mixpanel.track(AiSearchTrackingEvents.AI_SEARCH_APPLIED, {
        method: 'ai',
        is_talent_discovery: true,
        source: options?.source,
        filter_count: resolvedFilters.length,
        filter_ids: resolvedFilters.map((f) => f.id),
        filter_types: [...new Set(resolvedFilters.map((f) => f.type))],
        unknown_filter_count: mergedUnknownFilters.length,
        unknown_filter_names: mergedUnknownFilters.map((f) => f.name),
        fallback_to_keywords_count: mergedUnknownFilters.filter(
          (f) => 'fallbackToKeywords' in f && f.fallbackToKeywords === true
        ).length,
        page_id: revelioPageId ?? pageConfig.id,
      });

      return {
        adaptedFilters: resolvedFilters,
        unknownFilters: mergedUnknownFilters,
        blockedCompanyNames: blockedNames,
        droppedSelectionNames,
      };
    },
    [
      selectionLists,
      getFilterData,
      defaultSelectionOverrides,
      filterState,
      primaryFilterConfig,
      revelioPageId,
      viewGranularity,
      pageConfig.id,
      showCompatibilityToast,
      showTrialCompanyRestrictionToast,
      restrictToTrialCompanies,
      dispatch,
      gqlClient,
      isCompositionV4Enabled,
      geographyTaxonomyId,
      activityTaxonomyConfig,
      renderedPresetDateAnchor,
      loggedInUser,
    ]
  );

  const isValuationEnabled = useUnleashFlag(
    FeatureFlag.CompanyDiscoveryValuationFilter
  );
  const applyScreenerAiSearchFilters = useCallback(
    (
      response: CompanyDiscoveryAiOutput,
      options?: {
        /**
         * When true, seed from the page's CURRENT filters so this apply ADDS
         * to / overrides them rather than replacing the whole set — the
         * additive refinement path, mirroring `applyAiSearchFilters`.
         */
        mergeWithExistingFilters?: boolean;
        /** Surface attribution for the `ai search applied` event. */
        source?: AiSearchApplySource;
      }
    ): ApplyScreenerAiSearchFiltersResult => {
      const { filters: rawCustomDashFilters, unknownFilters } =
        transformScreenerAiResponseToCustomDashFilters(response);

      const customDashFilters = isValuationEnabled
        ? rawCustomDashFilters
        : rawCustomDashFilters.filter((f) => f.id !== FilterId.VALUATION);

      // Compute the consistency (group) key up front: the additive seed and the
      // taxonomy carry-over both need it to locate the page's EXISTING filters.
      // The group map is keyed by the consistency key —
      // the page id for screener-v2 pages, `primaryFilterConfig.id` otherwise
      // (see `applyAiSearchFilters` for the full rationale). Keying the seed by
      // `primaryFilterConfig.id` alone missed the screener-v2 group entirely
      // (its key is the page id), so a refinement seeded from defaults and wiped
      // the page's prior filters.
      const consistencyKey = getConsistencyKey({
        revelioPageId,
        primaryFilterId: primaryFilterConfig?.id,
      });

      // Seed the additive merge from the TARGET group's filters (keyed by the
      // consistency key), not `activeFilterMap` — which during navigation is
      // still the previous page's group (cross-scope leak). Falls back to
      // `activeFilterMap` when the group hasn't been populated yet.
      const targetGroupFilterMap = filterState.groupMap.get(consistencyKey);
      const baseFilterMap = options?.mergeWithExistingFilters
        ? new Map(targetGroupFilterMap ?? activeFilterMap)
        : createDefaultFilterMap(
            primaryFilterConfig?.id,
            activityTaxonomyConfig,
            defaultSelectionOverrides
          );
      // Carry over the target group's taxonomy filters (read from the target
      // group, not `activeFilterMap`, for the same cross-scope reason).
      [
        FilterId.ROLE_TAXONOMY,
        FilterId.SKILL_TAXONOMY,
        FilterId.ACTIVITY_TAXONOMY,
      ].forEach((filterId) => {
        const existing = targetGroupFilterMap?.get(filterId);
        if (existing) {
          baseFilterMap.set(filterId, existing);
        }
      });

      const filterMap: FilterSelectionsMap = new Map(baseFilterMap);
      customDashFilters.forEach((filter) => {
        filterMap.set(filter.id, filter);
      });

      dispatch({
        type: 'REPLACE_FILTERS',
        payload: {
          filterMap,
          consistencyKey,
        },
      });

      mixpanel.track(AiSearchTrackingEvents.AI_SEARCH_APPLIED, {
        method: 'ai',
        is_talent_discovery: false,
        source: options?.source,
        filter_count: customDashFilters.length,
        filter_ids: customDashFilters.map((f) => f.id),
        filter_types: [...new Set(customDashFilters.map((f) => f.type))],
        unknown_filter_count: unknownFilters.length,
        unknown_filter_names: unknownFilters.map((f) => f.name),
        page_id: revelioPageId ?? pageConfig.id,
      });

      return {
        adaptedFilters: customDashFilters,
        unknownFilters,
      };
    },
    [
      activeFilterMap,
      filterState,
      defaultSelectionOverrides,
      primaryFilterConfig,
      revelioPageId,
      pageConfig.id,
      dispatch,
      isValuationEnabled,
      activityTaxonomyConfig,
    ]
  );

  return { applyAiSearchFilters, applyScreenerAiSearchFilters };
};
